import { useCallback, useState } from 'react';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';

// ADR 0069: the Ranking page's Excel export. A POST (the ticked people are a list), so the browser can't download it by link: it arrives
// as a blob and is saved under the name the server gave it. A click never waits: the button greys and a toast says it is being made.

export interface ExportedFile {
  readonly fileName: string;
  readonly people: number;
  readonly sheets: number;
}

const FALLBACK_NAME = 'ranking.xlsx';
const TOAST_KEY = 'ranking-export';

/** The name in a `content-disposition`: the UTF-8 form when there is one, else the plain one. */
export function fileNameFrom(disposition: string | null): string {
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition ?? '');
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      // Fall through to the plain name.
    }
  }
  return /filename="([^"]+)"/i.exec(disposition ?? '')?.[1] ?? FALLBACK_NAME;
}

function messageOf(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null || !('error' in payload)) return null;
  const error = payload.error;
  if (typeof error !== 'object' || error === null || !('message' in error)) return null;
  return typeof error.message === 'string' ? error.message : null;
}

function countOf(header: string | null): number {
  const count = Number(header);
  return Number.isFinite(count) ? count : 0;
}

/** Everyone in the job (no ids), or only the ticked people. Resolves once the browser has been handed the file. */
export async function downloadRankingExport(jobId: string, applicationIds?: readonly string[]): Promise<ExportedFile> {
  let response: Response;
  try {
    response = await fetch(`/api/v1/jobs/${jobId}/ranking/export`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(applicationIds ? { applicationIds } : {}),
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError({ code: 'network_unreachable', message: "Couldn't reach the server. Check your connection and try again.", status: 0 });
  }
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    throw new ApiError({ code: 'export_failed', message: messageOf(payload) ?? "Couldn't make the Excel file. Try again.", status: response.status });
  }
  const fileName = fileNameFrom(response.headers.get('content-disposition'));
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
  return { fileName, people: countOf(response.headers.get('x-export-people')), sheets: countOf(response.headers.get('x-export-sheets')) };
}

function peopleWords(count: number): string {
  return `${count.toLocaleString('en-US')} ${count === 1 ? 'person' : 'people'}`;
}

export interface RankingExporter {
  readonly isPreparing: boolean;
  /** No ids: everyone in the job. Ids: only those people. A press while a file is being made does nothing. */
  readonly run: (applicationIds?: readonly string[]) => void;
}

export function useRankingExport(jobId: string): RankingExporter {
  const [isPreparing, setPreparing] = useState(false);
  const showToast = useToasts((state) => state.show);
  const run = useCallback(
    (applicationIds?: readonly string[]) => {
      if (isPreparing) return;
      setPreparing(true);
      showToast({ key: TOAST_KEY, tone: 'info', message: 'Preparing the Excel file…', detail: applicationIds ? `${peopleWords(applicationIds.length)} you ticked. You can keep working.` : 'Everyone in this job. You can keep working.' });
      downloadRankingExport(jobId, applicationIds)
        .then((file) => showToast({ key: TOAST_KEY, tone: 'success', message: 'Excel file ready', detail: `${peopleWords(file.people)} · ${file.sheets} ${file.sheets === 1 ? 'sheet' : 'sheets'}. Your browser saves ${file.fileName}.` }))
        .catch((error: unknown) => showToast({ key: TOAST_KEY, tone: 'danger', message: "The Excel file wasn't made", detail: errorMessage(error) }))
        .finally(() => setPreparing(false));
    },
    [isPreparing, jobId, showToast],
  );
  return { isPreparing, run };
}
